import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

// `size` também é atributo nativo de `<input>` (largura em caracteres, que ninguém usa
// aqui) — omitido para o nome valer o mesmo que no `Button`.
interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'size'> {
  label: string;
  /**
   * Esconde o rótulo visualmente, sem removê-lo: quando o ícone e o placeholder já
   * dizem o que é o campo, o texto acima virava repetição. O `<label>` continua no
   * DOM (`sr-only`) — leitor de tela e teste continuam encontrando o campo por ele.
   */
  hideLabel?: boolean;
  /**
   * `md` é o campo padrão do painel; `sm` é um pouco mais baixo e com texto menor, para
   * formulário curto onde o campo não precisa ter o peso de um da tabela.
   */
  size?: 'md' | 'sm';
  /**
   * `false` tira o halo de foco, deixando o destaque só na borda — que fica um tom mais
   * escura para compensar. Não é "campo sem indicação de foco": sem nenhuma marca não se
   * enxerga onde está o cursor ao navegar por teclado.
   */
  focusRing?: boolean;
  /** Mensagem de erro; quando presente, marca o campo como inválido. */
  error?: string | null;
  hint?: string;
  /** Ícone decorativo à esquerda. */
  adornment?: ReactNode;
}

const SIZES = {
  md: 'h-11 text-sm',
  sm: 'h-10 text-[0.8125rem]',
} as const;

export function Input({ label, hideLabel = false, size = 'md', focusRing = true, error, hint, adornment, className, ...props }: InputProps) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className={hideLabel ? 'sr-only' : 'text-[0.8125rem] font-medium text-ink-muted'}>
        {label}
      </label>

      <div className="relative">
        {adornment ? (
          <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-subtle [&>svg]:size-[1.125rem]">{adornment}</span>
        ) : null}

        <input
          id={id}
          aria-invalid={error ? true : undefined}
          {...(describedBy ? { 'aria-describedby': describedBy } : {})}
          className={cn(
            'w-full rounded-lg border bg-surface text-ink transition-shadow duration-150 placeholder:text-ink-subtle',
            SIZES[size],
            'focus:outline-none',
            focusRing && 'focus:shadow-focus',
            adornment ? 'pl-10 pr-3' : 'px-3',
            error
              ? 'border-rejected focus:border-rejected'
              : // Sem halo, a borda é a única marca de foco: um tom mais escuro para ela
                // não se confundir com o estado de repouso.
                cn('border-line-strong', focusRing ? 'focus:border-brand-500' : 'focus:border-brand-700'),
            className,
          )}
          {...props}
        />
      </div>

      {error ? (
        <p id={`${id}-error`} role="alert" className="text-[0.8125rem] text-rejected">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-[0.8125rem] text-ink-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
